import type { ActionInput, ActionName, ActionResult, ConnectorStatus } from "@agency/core";
import { useSyncExternalStore } from "react";
import type { BrandTokens } from "./theme";

export type ActorSource = "ui" | "faraday";
export type AuthMode = "dev" | "clerk" | "passcode";

export interface AppConfig {
  agency: {
    id: string;
    displayName: string;
    legalName: string;
    dba?: string;
    tagline: { en: string; es: string };
    brand: BrandTokens;
    supportedLocales: Array<"en" | "es">;
    defaultLocale: "en" | "es";
    timezone: string;
    state: string;
    offices: Array<{ name: string; address: string; phone: string }>;
    contact: { phone: string; email: string; website: string };
    features: { pipelines: boolean; gamification: boolean; publicLeaderboard: boolean; texting: boolean; faradayPanel: boolean };
    lines: string[];
    carriers: Array<{ name: string; lines: string[]; download: string }>;
    fictional: boolean;
  };
  user: { id: string; name: string; email: string; role: string; locale: "en" | "es" };
  users: Array<{ id: string; name: string; role: string }>;
  connectors: ConnectorStatus[];
  authMode: AuthMode;
  setup: { completed: boolean; skipped: boolean; step: number; startingData: "sample" | "import" | "empty" | null };
  publicIntakeUrl: string;
}

// ---------------------------------------------------------------------------------------------------------------------
// Session: dev user header (local), passcode session token (private demo), or a Clerk token provider.
// ---------------------------------------------------------------------------------------------------------------------

const DEV_USER_KEY = "agency.devUser";
const SESSION_KEY = "agency.session";

export function getDevUser(): string {
  try {
    return localStorage.getItem(DEV_USER_KEY) || "usr_gustavo";
  } catch {
    return "usr_gustavo";
  }
}

export function setDevUser(id: string) {
  try {
    localStorage.setItem(DEV_USER_KEY, id);
  } catch {
    /* ignore */
  }
}

interface StoredSession { token: string; expiresAt: string; user: { id: string; name: string; email: string; role: string } }

const listeners = new Set<() => void>();
function notify() { for (const l of listeners) l(); }

export function getSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (!s.token || (s.expiresAt && new Date(s.expiresAt).getTime() < Date.now())) return null;
    return s;
  } catch {
    return null;
  }
}

export function setSession(s: StoredSession | null) {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

/** Clerk (or any identity provider) registers how to get a fresh token; passcode sessions do not need one. */
let tokenProvider: (() => Promise<string | null>) | null = null;
let providerSignOut: (() => Promise<void>) | null = null;
export function setTokenProvider(get: (() => Promise<string | null>) | null, signOutFn: (() => Promise<void>) | null = null) {
  tokenProvider = get;
  providerSignOut = signOutFn;
}

/** Ends the session everywhere it lives and returns to the sign-in page. */
export async function signOut() {
  setSession(null);
  try { await providerSignOut?.(); } catch { /* ignore */ }
  window.location.assign("/sign-in");
}

/** React hook: the passcode session (if any) and a sign-out function. Clerk-mode callers get `session: null` and still can sign out. */
export function useSession(): { session: StoredSession | null; signedIn: boolean; signOut: () => Promise<void> } {
  const session = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => { try { return localStorage.getItem(SESSION_KEY) ?? ""; } catch { return ""; } },
    () => "",
  );
  const parsed = session ? getSession() : null;
  return { session: parsed, signedIn: !!parsed || !!tokenProvider, signOut };
}

async function bearer(): Promise<string | null> {
  if (tokenProvider) {
    try { return await tokenProvider(); } catch { return null; }
  }
  return getSession()?.token ?? null;
}

async function headers(source: ActorSource): Promise<Record<string, string>> {
  const h: Record<string, string> = { "content-type": "application/json", "x-dev-user": getDevUser(), "x-actor-source": source };
  const token = await bearer();
  if (token) h.authorization = `Bearer ${token}`;
  return h;
}

/** Auth headers for callers outside this module (the assistant's streaming request). */
export const apiHeaders = headers;

export class ApiError extends Error {
  constructor(message: string, public status: number, public operationId?: string) {
    super(message);
  }
}

/** A 401 means the session is gone: clear it and go to sign-in (unless we are already there). */
function onUnauthorized() {
  setSession(null);
  if (window.location.pathname !== "/sign-in") {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/sign-in?next=${next}`);
  }
}

export async function fetchConfig(): Promise<AppConfig> {
  const res = await fetch("/api/config", { headers: await headers("ui") });
  if (res.status === 401) { onUnauthorized(); throw new ApiError("Sign in required", 401); }
  if (!res.ok) {
    let msg = `Config failed (${res.status})`;
    try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* keep */ }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as AppConfig;
}

export interface PublicConfig {
  agency: {
    id: string;
    displayName: string;
    tagline: { en: string; es: string };
    brand: BrandTokens;
    offices: Array<{ name: string; address: string; phone: string }>;
    contact: { phone: string; email: string; website: string };
    lines: string[];
    supportedLocales: Array<"en" | "es">;
    defaultLocale: "en" | "es";
  };
  auth: { mode: AuthMode; clerkPublishableKey: string | null };
}

/** Public, unauthenticated: brand + contact for the website intake page and the sign-in page, plus the auth mode. */
export async function fetchPublicConfig(): Promise<PublicConfig> {
  const res = await fetch("/api/public/config");
  if (!res.ok) throw new ApiError(`Public config failed (${res.status})`, res.status);
  return (await res.json()) as PublicConfig;
}

/** Passcode mode: exchange the access code for a session. Stores it on success. */
export async function signInWithPasscode(input: { passcode: string; name: string; email: string }): Promise<{ ok: true; user: StoredSession["user"] } | { ok: false; error: string; status: number }> {
  const res = await fetch("/api/public/session", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input) });
  let body: { ok?: boolean; token?: string; expiresAt?: string; user?: StoredSession["user"]; error?: string } = {};
  try { body = (await res.json()) as typeof body; } catch { body = {}; }
  if (!res.ok || !body.ok || !body.token || !body.user) return { ok: false, error: body.error ?? `Sign-in failed (${res.status})`, status: res.status };
  setSession({ token: body.token, expiresAt: body.expiresAt ?? new Date(Date.now() + 12 * 3600_000).toISOString(), user: body.user });
  return { ok: true, user: body.user };
}

export interface IntakeSubmission {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  line: "auto" | "home" | "renters" | "commercial_auto" | "general_liability" | "bop" | "life" | "health";
  locale: "en" | "es";
  notes?: string;
  smsConsent: boolean;
  consentText: string;
  website?: string;
}

export async function submitIntake(body: IntakeSubmission): Promise<{ ok: true; customerId: string } | { ok: false; error: string }> {
  const res = await fetch("/api/public/intake", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  try {
    return (await res.json()) as { ok: true; customerId: string } | { ok: false; error: string };
  } catch {
    return { ok: false, error: `Request failed (${res.status})` };
  }
}

/** Public, no auth: the customer's self-service page and the signing page. The token in the URL is the key. */
async function publicJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, body === undefined ? undefined : { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(json.error ?? `Request failed (${res.status})`, res.status);
  return json;
}
export interface PortalView {
  agency: { displayName: string; brand: PublicConfig["agency"]["brand"]; offices: PublicConfig["agency"]["offices"]; contact: PublicConfig["agency"]["contact"]; supportedLocales: Array<"en" | "es"> };
  customer: { firstName: string; locale: "en" | "es"; agentName: string | null };
  policies: Array<{ id: string; carrier: string; line: string; policyNumber: string; status: string; effectiveDate: string; expirationDate: string }>;
  openRequests: Array<{ kind: string; status: string; createdAt: string }>;
  payments: Array<{ receiptNumber: string; amountCents: number; method: string; takenAt: string }>;
  smsConsent: boolean;
}
export const fetchPortal = (token: string) => publicJson<PortalView>(`/api/public/portal/${encodeURIComponent(token)}`);
export const portalRequest = (token: string, body: { kind: "id_card" | "proof" | "change" | "question" | "callback"; details: string; policyId?: string; locale: "en" | "es" }) => publicJson<{ ok: true }>(`/api/public/portal/${encodeURIComponent(token)}/request`, body);
export const portalUpload = (token: string, body: { title: string; contentType: string; base64: string }) => publicJson<{ ok: true }>(`/api/public/portal/${encodeURIComponent(token)}/upload`, body);
export const portalConsent = (token: string, body: { agree: boolean; locale: "en" | "es"; consentText: string }) => publicJson<{ ok: true; smsConsent: boolean }>(`/api/public/portal/${encodeURIComponent(token)}/consent`, body);
export interface SignView {
  agency: { displayName: string; brand: PublicConfig["agency"]["brand"]; contact: PublicConfig["agency"]["contact"]; supportedLocales: Array<"en" | "es"> };
  request: { id: string; title: string; purpose: string; status: string; expiresAt: string; signedAt: string | null; signerName?: string };
  signerName: string;
  locale: "en" | "es";
  documentUrl: string | null;
  consent: { version: string; en: string; es: string };
}
export const fetchSignRequest = (token: string) => publicJson<SignView>(`/api/public/sign/${encodeURIComponent(token)}`);
export const submitSignature = (token: string, body: { signerName: string; signaturePng: string; agree: boolean; locale: "en" | "es"; decline?: boolean; declineReason?: string }) => publicJson<{ ok: true; status: "signed" | "declined"; signedUrl?: string }>(`/api/public/sign/${encodeURIComponent(token)}`, body);

/** Typed call into the one action API. Screens and the Faraday panel both use this. */
export async function callAction<N extends ActionName, R = unknown>(
  name: N,
  input: ActionInput<N>,
  opts: { source?: ActorSource } = {},
): Promise<ActionResult<R>> {
  const res = await fetch(`/api/actions/${name}`, {
    method: "POST",
    headers: await headers(opts.source ?? "ui"),
    body: JSON.stringify(input ?? {}),
  });
  if (res.status === 401) { onUnauthorized(); throw new ApiError("Sign in required", 401); }
  let body: ActionResult<R> | { error?: string } | null = null;
  try {
    body = (await res.json()) as ActionResult<R>;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = (body && "error" in body && body.error) || `${name} failed (${res.status})`;
    throw new ApiError(msg, res.status, body && "operationId" in body ? body.operationId : undefined);
  }
  const result = body as ActionResult<R>;
  if (result.status === "failed") throw new ApiError(result.error ?? `${name} failed`, 500, result.operationId);
  return result;
}
